import dayjs from 'dayjs';

export const BUSINESS_TZ = 'America/Mexico_City';

// CALENDAR DATE field: the stored value is a bare day stamped at midnight UTC.
// Read the date part back as UTC so the day never shifts.
export const formatDate = (date: string | null): string => {
    return date ? dayjs.utc(date).format(DATE_FORMAT) : '-';
};

// INSTANT field: convert UTC → Mexico business time for display.
export const formatDateTime = (date: string | null): string => {
    return date ? dayjs.utc(date).tz(BUSINESS_TZ).format(DATE_TIME_FORMAT) : '-';
};

// INSTANT field: elapsed time is timezone-independent.
export const formatRelativeDate = (date: string): string => {
    return dayjs.utc(date).fromNow();
};

// Short human date with the relative phrase in parens, e.g.
// "mié 20 jun (hace 3 días)" — matches the Saldos y pagos column style. Built
// on the same UTC basis as formatRelativeDate so date and relative agree.
//
// CALENDAR DATE field: this is the reader for values stamped at midnight UTC
// (transferred_date and friends). A true timestamp wants
// formatBusinessDateWithRelative below instead.
// INSTANT field: short human date in Mexico business time + relative phrase.
export const formatDateWithRelative = (date: string | null): string => {
    if (!date) return '-';
    const d = dayjs.utc(date).tz(BUSINESS_TZ);
    return `${d.format('ddd D MMM').replace(/\./g, '')} (${dayjs.utc(date).fromNow()})`;
};

/**
 * The company's business day. Merida runs on Mexico City's offset, and this is
 * the zone the activities date filter interprets its bounds in (see the backend
 * helper of the same name). Naming the zone rather than hard-coding -06:00 keeps
 * this correct if Mexico ever restores DST.
 */
export const BUSINESS_TIME_ZONE = 'America/Mexico_City';

// INSTANT field shown on the business day it belongs to. Same shape as
// formatDateWithRelative — "mié 20 jun (hace 3 días)" — but read in Mexico City
// rather than in UTC.
//
// The distinction is load-bearing for the activities feed: an activity written
// at 19:00 in Merida is already the NEXT day in UTC, so the UTC reader showed it
// under tomorrow's date while the date filter (which works in Mexico City days)
// returned it under today's. One of the two had to move, and the filter is the
// one that matches how the business thinks about a day.
export const formatBusinessDateWithRelative = (date: string | null): string => {
    if (!date) return '-';
    const d = dayjs.utc(date);
    return `${d
        .tz(BUSINESS_TIME_ZONE)
        .format('ddd D MMM')
        .replace(/\./g, '')} (${d.fromNow()})`;
};

// INSTANT field with its time of day, on the business day it belongs to.
// Used by the audit history panel, where the exact moment of a change matters.
export const formatBusinessDateTime = (date: string | null): string => {
    return date
        ? dayjs.utc(date).tz(BUSINESS_TIME_ZONE).format('DD/MM/YYYY HH:mm')
        : '-';
};

export const DATE_FORMAT = 'YYYY-MM-DD';
export const DATE_MASK = '____-__-__';

// HOURLY_DATA_EPOCH moved to app/smart/production-performance/window.ts on
// 2026-08-11: it is a fact about hour capture on productions, not about date
// formatting, and now lives beside the grading rules that use it.

export const YEAR_FORMAT = 'YYYY';
export const YEAR_MASK = '____';

export const MONTH_FORMAT = 'YYYY-MM';
export const MONTH_HUMAN_FORMAT = 'MMMM';
export const MONTH_MASK = '____-__';

export const YEAR_MONTH_FORMAT = 'YYYY-MM';
export const YEAR_MONTH_MASK = '____-__';

const TIME_FORMAT = 'HH:mm:ss';
const TIME_MASK = '__:__:__';

export const DATE_TIME_MASK = DATE_MASK + ' ' + TIME_MASK;
export const DATE_TIME_FORMAT = DATE_FORMAT + ' ' + TIME_FORMAT;

const TIME_HOUR_MINUTE_FORMAT = 'hh:mm A';
export const DATE_HOUR_MINUTE_FORMAT = DATE_FORMAT + ' ' + TIME_HOUR_MINUTE_FORMAT;

// INSTANT field: convert UTC → Mexico business time for display.
export const formatDateHourMinute = (date: string | null): string => {
    return date
        ? dayjs.utc(date).tz(BUSINESS_TZ).format(DATE_HOUR_MINUTE_FORMAT)
        : '-';
};

// CALENDAR DATE field (floating, no real time): the stored value is a bare
// day stamped at midnight UTC. Read the date part back as UTC so the day never
// shifts, regardless of the viewer's timezone. (Pairs with getStringFromDate.)
export function getDateFromString(str: string) {
    return dayjs.utc(str).format('YYYY-MM-DD');
}

// INSTANT field: convert UTC instant → Mexico business-time wall clock for the
// edit form. (Pairs with getStringFromDateTime.)
export function getDateTimeFromString(str: string) {
    return dayjs.utc(str).tz(BUSINESS_TZ).format(DATE_TIME_FORMAT);
}

// INSTANT field: return the Mexico business calendar day for a UTC instant.
// This is useful when a ledger intentionally displays only the day while
// still needing to classify a payment such as 03:00Z as the prior Mexico day.
export function getBusinessDateFromInstant(str: string) {
    return dayjs.utc(str).tz(BUSINESS_TZ).format(DATE_FORMAT);
}

// CALENDAR DATE field: keep the day as a floating value — stamp it at midnight
// UTC with no timezone conversion, so it round-trips unchanged everywhere.
export function getStringFromDate(date: string, time: string = '00:00:00') {
    return `${date}T${time}.000Z`;
}

// INSTANT field: the input is Mexico business-time wall clock. Parse it
// explicitly in America/Mexico_City and convert to a true UTC instant.
export function getStringFromDateTime(datetime: string) {
    return dayjs.tz(datetime, DATE_TIME_FORMAT, BUSINESS_TZ).utc().toISOString();
}
